import React, { useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { ApiError } from '../../api/client';
import { EMAIL_RE, clearStoredAuthIfApiError, readStoredAuth, writeStoredAuth } from './profileFormUtils';
import {
  formatPhoneDigitsForAuthLookup,
  formatPhoneForDisplay,
  sanitizePhoneDigitsInput,
  validatePhoneDigitsValue,
} from '../../utils/phone';
import {
  confirmCurrentIdentity,
  confirmNewIdentity,
  getIdentityChangeStatus,
  proposeIdentityChange,
  startIdentityChange,
} from './accountIdentityApi';
import './AccountLayout.css';

const titles = { email: 'Change email', phone: 'Change mobile' };

function messageFromError(error) {
  if (error instanceof ApiError) return error.message;
  return error?.message || 'Something went wrong. Please try again.';
}

export default function AccountIdentityChangeModal({
  changeType,
  currentEmail = '',
  currentPhone = '',
  onClose,
  onCompleted,
}) {
  const [step, setStep] = useState('ask-mobile');
  const [stillHasMobile, setStillHasMobile] = useState(null);
  const [requestToken, setRequestToken] = useState('');
  const [destination, setDestination] = useState('');
  const [channel, setChannel] = useState('');
  const [currentOtp, setCurrentOtp] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newOtp, setNewOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const contactLabel = changeType === 'email' ? 'email address' : 'mobile number';
  const phoneDisplay = formatPhoneForDisplay(currentPhone) || String(currentPhone || '').trim();
  const emailDisplay = String(currentEmail || '').trim();
  const hasPhoneOnAccount = Boolean(String(currentPhone || '').replace(/\D/g, '').length);
  const hasEmailOnAccount = Boolean(emailDisplay);
  const token = () => readStoredAuth().token;

  const run = async (operation) => {
    setBusy(true);
    setError('');
    try {
      await operation();
    } catch (err) {
      clearStoredAuthIfApiError(err);
      setError(messageFromError(err));
    } finally {
      setBusy(false);
    }
  };

  const chooseStillHasMobile = (value) => {
    setError('');
    setMessage('');
    if (value === true && !hasPhoneOnAccount) {
      setError('No mobile number is on this account. Choose No to verify with your email.');
      return;
    }
    if (value === false && !hasEmailOnAccount) {
      setError('No email is on this account. Choose Yes to verify with your mobile.');
      return;
    }
    setStillHasMobile(value);
    setStep('current');
  };

  const sendCurrentCode = () =>
    run(async () => {
      if (stillHasMobile == null) {
        throw new Error('Please confirm whether you still have your current mobile number.');
      }
      const { data } = await startIdentityChange(token(), changeType, stillHasMobile);
      setRequestToken(data.token);
      setDestination(data.destination);
      setChannel(data.channel);
      setMessage('Verification code sent.');
    });

  const verifyCurrent = () =>
    run(async () => {
      if (!/^\d{6}$/.test(currentOtp.trim())) {
        throw new Error('Enter the 6-digit verification code.');
      }
      await confirmCurrentIdentity(token(), requestToken, currentOtp.trim());
      setStep('new');
      setMessage('Current contact confirmed. Enter your new details.');
    });

  const sendNewCode = () =>
    run(async () => {
      const value =
        changeType === 'email'
          ? newValue.trim().toLowerCase()
          : formatPhoneDigitsForAuthLookup('91', sanitizePhoneDigitsInput(newValue));
      if (changeType === 'email' && !EMAIL_RE.test(value)) {
        throw new Error('Enter a valid email address.');
      }
      if (changeType === 'phone' && !validatePhoneDigitsValue(value).ok) {
        throw new Error(validatePhoneDigitsValue(value).message);
      }
      const { data } = await proposeIdentityChange(token(), requestToken, value);
      setNewValue(value);
      setDestination(data.destination);
      setChannel(data.channel);
      setStep('verify-new');
      setMessage(
        changeType === 'email'
          ? 'We sent a code and confirmation link to your new email.'
          : 'Verification code sent to your new mobile number.',
      );
    });

  const complete = (data) => {
    const auth = readStoredAuth();
    writeStoredAuth({
      user: {
        ...(auth.user || {}),
        ...(data.email ? { email: data.email } : {}),
        ...(data.phone_number ? { mobile: data.phone_number } : {}),
      },
    });
    setStep('done');
    setMessage(`${changeType === 'email' ? 'Email' : 'Mobile number'} updated successfully.`);
    onCompleted?.(data);
  };

  const verifyNew = () =>
    run(async () => {
      if (!/^\d{6}$/.test(newOtp.trim())) {
        throw new Error('Enter the 6-digit verification code.');
      }
      const { data } = await confirmNewIdentity(token(), requestToken, newOtp.trim());
      complete(data);
    });

  const checkEmailLink = () =>
    run(async () => {
      const { data } = await getIdentityChangeStatus(token(), requestToken);
      if (!data.completed) {
        throw new Error(
          'Your email has not been confirmed yet. Use the code or open the link in the email.',
        );
      }
      complete(data);
    });

  return (
    <div className="account-sub-detail-overlay" role="presentation" onMouseDown={onClose}>
      <div
        className="account-sub-detail-modal account-identity-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="identity-change-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="account-sub-detail-modal__header">
          <div>
            <h2 id="identity-change-title">{titles[changeType]}</h2>
            <p>For your security, verify your current contact before making this change.</p>
          </div>
          <button
            type="button"
            className="account-sub-detail-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes aria-hidden />
          </button>
        </header>
        <div className="account-sub-detail-modal__body">
          {message ? <p className="auth-inline-message auth-inline-message--success">{message}</p> : null}
          {error ? (
            <p className="account-page__field-error" role="alert">
              {error}
            </p>
          ) : null}

          {step === 'ask-mobile' ? (
            <section>
              <h3>1. Confirm access to your current mobile</h3>
              <p className="account-page__muted">
                Do you still have access to your current mobile number
                {phoneDisplay ? (
                  <>
                    {' '}
                    (<strong>{phoneDisplay}</strong>)
                  </>
                ) : null}
                ?
              </p>
              <p className="account-page__muted">
                {hasPhoneOnAccount
                  ? 'Yes sends a code by SMS to that mobile. No sends a code to your current email instead.'
                  : 'No mobile is on this account, so choose No to verify with your current email.'}
              </p>
            </section>
          ) : null}

          {step === 'current' ? (
            <section>
              <h3>2. Verify your current contact</h3>
              {requestToken ? (
                <>
                  <p className="account-page__muted">
                    Enter the code sent by {channel === 'sms' ? 'SMS' : 'email'} to{' '}
                    <strong>{destination}</strong>.
                  </p>
                  <label className="account-identity-modal__label" htmlFor="identity-current-otp">
                    Verification code
                  </label>
                  <input
                    id="identity-current-otp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={currentOtp}
                    onChange={(event) => setCurrentOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  />
                </>
              ) : (
                <p className="account-page__muted">
                  {stillHasMobile
                    ? `We will send a verification code by SMS to ${phoneDisplay || 'your current mobile'}.`
                    : `We will send a verification code to ${emailDisplay || 'your current email'}.`}
                </p>
              )}
            </section>
          ) : null}

          {step === 'new' ? (
            <section>
              <h3>3. Enter your new {contactLabel}</h3>
              <label className="account-identity-modal__label" htmlFor="identity-new-value">
                New {contactLabel}
              </label>
              <input
                id="identity-new-value"
                type={changeType === 'email' ? 'email' : 'tel'}
                autoComplete={changeType === 'email' ? 'email' : 'tel'}
                value={newValue}
                onChange={(event) =>
                  setNewValue(
                    changeType === 'phone'
                      ? sanitizePhoneDigitsInput(event.target.value)
                      : event.target.value,
                  )
                }
              />
            </section>
          ) : null}

          {step === 'verify-new' ? (
            <section>
              <h3>4. Confirm your new {contactLabel}</h3>
              <p className="account-page__muted">
                Enter the code sent to <strong>{destination}</strong>.
                {changeType === 'email' ? ' You can also use the confirmation link in that email.' : ''}
              </p>
              <label className="account-identity-modal__label" htmlFor="identity-new-otp">
                Verification code
              </label>
              <input
                id="identity-new-otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={newOtp}
                onChange={(event) => setNewOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </section>
          ) : null}
        </div>
        <footer className="account-sub-detail-modal__footer">
          {step === 'ask-mobile' ? (
            <>
              <button
                type="button"
                className="account-sub-detail-modal__secondary"
                disabled={busy || !hasEmailOnAccount}
                onClick={() => chooseStillHasMobile(false)}
              >
                No, use email
              </button>
              <button
                type="button"
                className="account-update-page__save-btn"
                disabled={busy || !hasPhoneOnAccount}
                onClick={() => chooseStillHasMobile(true)}
              >
                Yes, I still have it
              </button>
            </>
          ) : null}
          {step === 'current' ? (
            <>
              {!requestToken ? (
                <button
                  type="button"
                  className="account-sub-detail-modal__secondary"
                  disabled={busy}
                  onClick={() => {
                    setStillHasMobile(null);
                    setStep('ask-mobile');
                    setError('');
                    setMessage('');
                  }}
                >
                  Back
                </button>
              ) : null}
              <button
                type="button"
                className="account-update-page__save-btn"
                disabled={busy}
                onClick={requestToken ? verifyCurrent : sendCurrentCode}
              >
                {busy ? 'Please wait…' : requestToken ? 'Confirm current contact' : 'Send verification code'}
              </button>
            </>
          ) : null}
          {step === 'new' ? (
            <button
              type="button"
              className="account-update-page__save-btn"
              disabled={busy}
              onClick={sendNewCode}
            >
              {busy ? 'Please wait…' : `Send code to new ${changeType === 'email' ? 'email' : 'mobile'}`}
            </button>
          ) : null}
          {step === 'verify-new' ? (
            <>
              {changeType === 'email' ? (
                <button
                  type="button"
                  className="account-sub-detail-modal__secondary"
                  disabled={busy}
                  onClick={checkEmailLink}
                >
                  I used the email link
                </button>
              ) : null}
              <button
                type="button"
                className="account-update-page__save-btn"
                disabled={busy}
                onClick={verifyNew}
              >
                {busy ? 'Please wait…' : `Verify and change ${changeType === 'email' ? 'email' : 'mobile'}`}
              </button>
            </>
          ) : null}
          {step === 'done' ? (
            <button type="button" className="account-update-page__save-btn" onClick={onClose}>
              Done
            </button>
          ) : null}
        </footer>
      </div>
    </div>
  );
}
